// -------------------- Core Predicates ----------------------

import { ItemTops } from './types';

/**
 * True if scrollTop is within item index (0 <= index < n)
 */
const isWithinItem = (
  scrollTop: number,
  index: number,
  itemTops: ItemTops
): boolean => itemTops[index] <= scrollTop && scrollTop < itemTops[index + 1];

// -------------------- Bracketing (Galloping) ---------------

/**
 * Gallop FORWARD on item indices to bracket the answer.
 * Returns [low, high] with 0 <= low <= high <= (n-1), guaranteeing
 * the correct item index lies within that inclusive range.
 */
const bracketForwardOnItems = (
  scrollTop: number,
  startNumber: number,
  itemTops: ItemTops
): { low: number; high: number } => {
  const n = itemTops.length - 1; // number of items
  let low = startNumber;
  let high = startNumber;

  // Step once if the next boundary has been crossed.
  if (high < n - 1 && itemTops[high + 1] <= scrollTop) {
    high = high + 1;
  }

  // Exponential expansion while the *next* boundary is still <= scrollTop.
  let step = 1;
  while (high < n - 1 && itemTops[high + 1] <= scrollTop) {
    low = high;
    const nextHigh = high + step;
    high = nextHigh < n - 1 ? nextHigh : n - 1;
    step = step * 2;
  }

  return { low, high };
};

/**
 * Gallop BACKWARD on item indices to bracket the answer.
 * Returns [low, high] with 0 <= low <= high <= (n-1), guaranteeing
 * the correct item index lies within that inclusive range.
 */
const bracketBackwardOnItems = (
  scrollTop: number,
  startNumber: number,
  itemTops: ItemTops
): { low: number; high: number } => {
  let low = startNumber;
  let high = startNumber;

  // Step once left if we've moved above the current item's top.
  if (low > 0 && itemTops[low] > scrollTop) {
    low = low - 1;
  }

  // Exponential expansion while the CURRENT item's top is still > scrollTop.
  let step = 1;
  while (low > 0 && itemTops[low] > scrollTop) {
    high = low;
    const nextLow = low - step;
    low = nextLow > 0 ? nextLow : 0;
    step = step * 2;
  }

  return { low, high };
};

// -------------------- Binary Search -------------------

/**
 * Binary search on inclusive item index range [low, high] to find the
 * LAST index j such that itemTops[j] <= scrollTop.
 *
 * Because we never search beyond 0..(n-1), the return value is always
 * a valid item index.
 */
const findLastItemWhoseTopIsAbove = (
  scrollTop: number,
  rangeStart: number,
  rangeEnd: number,
  itemTops: ItemTops
): number => {
  let low = rangeStart;
  let high = rangeEnd;

  // the correct answer lies within [low, high].
  while (low < high) {
    // Upper-biased midpoint to find the "last true" efficiently.
    const mid = Math.floor((low + high + 1) / 2);
    if (itemTops[mid] <= scrollTop) {
      low = mid; // mid is valid; keep right side
    } else {
      high = mid - 1; // mid too large; move left
    }
  }
  return low;
};

// -------------------- Main API -----------------------------

type FinedItemIndexForScrollArgs = {
  scrollTop: number;
  currentIndex: number;
  itemTops: ItemTops;
};

/**
 * Find the index of the item whose top is <= scrollTop and whose bottom is > scrollTop.
 * Uses a "finger" (currentIndex) plus galloping (exponential) search to bracket,
 * then a tiny binary search inside the bracket.
 *
 * Complexity:
 *   - O(1) if still within the same item
 *   - O(log Δ) where Δ = items crossed since last frame
 *   - Worst-case O(log n) (same as regular binary search), but only on teleports
 */
export const findItemIndexForScroll = ({
  scrollTop,
  currentIndex,
  itemTops,
}: FinedItemIndexForScrollArgs): number => {
  if (!itemTops.length) return 0; // no items, the first item will be undefined

  // Fast path: still within the same item.
  if (isWithinItem(scrollTop, currentIndex, itemTops)) {
    return currentIndex;
  }

  // Decide direction by comparing against the next boundary.
  if (scrollTop >= itemTops[currentIndex + 1]) {
    const { low, high } = bracketForwardOnItems(
      scrollTop,
      currentIndex,
      itemTops
    );
    return findLastItemWhoseTopIsAbove(scrollTop, low, high, itemTops);
  }
  const { low, high } = bracketBackwardOnItems(
    scrollTop,
    currentIndex,
    itemTops
  );
  return findLastItemWhoseTopIsAbove(scrollTop, low, high, itemTops);
};
